import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { Divider, Space } from 'antd';

import { useUpdateBreadcrumbParam } from '@/contexts/BreadcrumbContextProvider';

import { usePart } from '@/core/domain/operational/Parts/Parts.hooks';

import { PartStockPricesResult } from '@/core/domain/stock/StockItems/StockItems.types';
import { usePartStockPrices } from '@/core/domain/stock/StockItems/StockItems.hooks';

import { quantityFormatter } from '@/utils/number';

import { PageProps } from '@/types/PageProps';

import {
  ColumnsType,
  DefaultRouteTabs,
  EditIcon,
  InfiniteTable,
  SaveButton,
} from '@/components/_common';
import CompanyPartStockPricesModal from '@/components/Operational/Inventory/Parts/CompanyPartStockPricesModal';
import PartDetailsDescription from '@/components/Operational/Inventory/Parts/PartDetailsDescription';

import { getTableScrollX } from '@/utils/table';
import { useInventoryPartFormContext } from '../../InventoryPartsProvider';
import PartFooter from '@/components/Operational/Inventory/Parts/PartFooter';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';
import formatterPrice from '@/utils/number/formatterPrice';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useHistory } from 'react-router-dom';
import PartDetailsStockCorrectionStockAction from '@/components/Operational/Inventory/Parts/Details/Stock/CorrectionStockAction';

function PartStockPage({ route }: PageProps) {
  const intl = useIntl();

  const params = useQueryParams<{ companyId?: string }>();
  const history = useHistory();

  const {
    handleSubmit,
    isLoading: contextLoading,
    disabled,
    partId,
  } = useInventoryPartFormContext();

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedPartStockPrices, setSelectedPartStockPrices] =
    useState<PartStockPricesResult>();

  const { data: part } = usePart(Number(partId));

  const { data, isLoading } = usePartStockPrices(Number(partId));

  useEffect(() => {
    if (params.companyId && !isLoading && data?.result.length) {
      const item = data.result.find(
        (item) => item.company.id === Number(params.companyId),
      );

      if (item) {
        handleModalVisible(item);
      }
    }
  }, [params, isLoading, data?.result]);

  useUpdateBreadcrumbParam(':partId', part?.name);

  const handleModalVisible = async (item?: PartStockPricesResult) => {
    if (item) {
      setSelectedPartStockPrices(item);
    } else {
      setSelectedPartStockPrices(undefined);
    }

    history.replace({ search: '' });
    setModalVisible(!modalVisible);
  };

  const columns: ColumnsType<PartStockPricesResult> = [
    {
      dataIndex: ['company', 'name'],
      title: intl.formatMessage({ id: 'parts.stock.domain.company' }),
      ellipsis: true,
    },
    {
      dataIndex: 'stock',
      title: intl.formatMessage({ id: 'parts.stock.domain.stock' }),
      align: 'right',
      width: 180,
      render: (value: number) => quantityFormatter(value),
    },
    {
      dataIndex: 'availableStock',
      title: intl.formatMessage({ id: 'parts.stock.domain.availableStock' }),
      align: 'right',
      width: 200,
      render: (value: number) => quantityFormatter(value),
    },
    {
      dataIndex: 'sellPrice',
      title: intl.formatMessage({ id: 'parts.stock.domain.sellPrice' }),
      align: 'right',
      width: 180,
      render: (value: number) =>
        !hasPermissions([Authorities.REGISTER_PART_VIEW_PRICES])
          ? '-'
          : formatterPrice(value),
    },
    {
      dataIndex: 'location',
      title: intl.formatMessage({ id: 'parts.stock.domain.location' }),
      align: 'right',
      width: 160,
      render: (value: string) => (
        <EllipsisHoverToggle>{value || '-'}</EllipsisHoverToggle>
      ),
      ellipsis: true,
    },
    {
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      width: 90,
      render: (_, record) => (
        <Space>
          <EditIcon
            iconProps={{ onClick: () => handleModalVisible(record) }}
            disabled={
              !hasPermissions(
                [
                  Authorities.REGISTER_PART_VIEW_PRICES,
                  Authorities.REGISTER_PART_EDIT_PRODUCT_PRICES,
                  Authorities.REGISTER_PART_EDIT_PRODUCT_STOCK,
                ],
                true,
              )
            }
          />

          <PartDetailsStockCorrectionStockAction partStockPricesItem={record} />
        </Space>
      ),
      fixed: 'right',
      align: 'right',
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <DefaultRouteTabs
        route={route.parent}
        permissions={route.permissions}
        baseKey={String(partId)}
        basePath={`/app/operational/inventory/parts/${partId}`}
        extra={
          <SaveButton
            block
            disabled={
              disabled || !hasPermissions([Authorities.REGISTER_PART_EDIT])
            }
            onClick={handleSubmit}
            loading={contextLoading}
          >
            {intl.formatMessage({ id: 'commons.entity.save' })}
          </SaveButton>
        }
      >
        <PartDetailsDescription isLoading={isLoading} part={part} />

        <Divider />

        <InfiniteTable
          columns={columns}
          dataSource={data?.result}
          loading={isLoading}
          pagination={false}
          rowKey={(row) => row.company.id}
          scroll={{ x: getTableScrollX(columns) }}
        />

        <PartFooter partId={Number(partId)} disabled={disabled} />
      </DefaultRouteTabs>

      <CompanyPartStockPricesModal
        visible={modalVisible}
        handleVisible={handleModalVisible}
        partStockPrices={selectedPartStockPrices}
      />
    </>
  );
}

export default PartStockPage;
